'use client'

import { DocumentCategory } from '@/app/api/types/redpim.types'
import CustomImage from '@/components/Image/CustomImage'
import { LanguageRecord } from '@/graphql/types'
import { cn } from '@desoutter/uikit'
import { useParams } from 'next/navigation'
import { useMemo } from 'react'

type SearchResult = {
	id: string
	title: React.ReactNode
	bodyExcerpt: React.ReactNode
	url: string
}

export type DisplayResult = SearchResult & {
	imageSrc?: string
}

export default function ResultsView({
	data,
	products,
	accessories,
	dt,
	resources,
	count,
	translations,
	isModal,
}: {
	data: SearchResult[]
	products: DisplayResult[]
	accessories: DisplayResult[]
	dt: {
		serialNumber: string
		partNumber: string
		family: string
		name: string
	} | null
	resources?: {
		itemPath: string
		documentCategory: DocumentCategory
		metadataStorageName: string
		metadataStorageSize: string
	}[]
	count: number
	translations: Partial<LanguageRecord>
	isModal?: boolean
}) {
	const { lang } = useParams()
	const allSiteSearchResults = (data || [])
		.filter((page) => !page.url.includes('/articles/'))
		.filter((page) => !page.url.includes('/products/series/'))
		.filter((page) => !page.url.includes('/solutions/'))
		.map((page) => ({
			...page,
			imageSrc: '/images/book-1.svg',
		})) as DisplayResult[]

	const procuctSearchResults = (data || [])
		.filter((product) => product.url.includes('/products/series/'))
		.map((product) => ({
			...product,
			imageSrc: '/images/box.svg',
		})) as DisplayResult[]

	const solutionSearchResults = (data || [])
		.filter((product) => product.url.includes('/solutions/'))
		.map((product) => ({
			...product,
			imageSrc: '/images/box.svg',
		})) as DisplayResult[]

	const articles = (data || [])
		.filter((article) => article.url.includes('/articles/'))
		.map((article) => ({
			...article,
			imageSrc: '/images/book.svg',
		})) as DisplayResult[]

	const documents = resources.map((resource) => ({
		id: resource.itemPath,
		title: `${translations.download} ${resource.metadataStorageName
			.split('.')
			.shift()}`,
		bodyExcerpt: `${resource.metadataStorageName
			.split('.')
			.pop()
			.toUpperCase()} · ${
			(Number(resource.metadataStorageSize) / (1024 * 1024)).toFixed(2) + ' MB'
		}`,
		imageSrc: '/images/document-download.svg',
		url: resource.itemPath,
		documentCategory: resource.documentCategory,
	}))

	const cadFiles = documents.filter(
		(doc) => doc.documentCategory === DocumentCategory.cadFiles,
	)
	const userManuals = documents.filter(
		(doc) => doc.documentCategory === DocumentCategory.userManuals,
	)
	const safetyInstructions = documents.filter(
		(doc) => doc.documentCategory === DocumentCategory.safetyInstructions,
	)
	const complianceCertificates = documents.filter(
		(doc) => doc.documentCategory === DocumentCategory.complianceCertificates,
	)
	const explodedViews = documents.filter(
		(doc) => doc.documentCategory === DocumentCategory.explodedViews,
	)

	const resultsCount = useMemo(() => {
		return (
			products.length +
			accessories.length +
			procuctSearchResults.length +
			solutionSearchResults.length +
			articles.length +
			documents.length +
			allSiteSearchResults.length
		)
	}, [
		products,
		procuctSearchResults,
		accessories,
		solutionSearchResults,
		articles,
		documents,
		allSiteSearchResults,
	])

	const documentTypes = useMemo(() => {
		const categoryName = documents.map((doc) => {
			switch (doc.documentCategory) {
				case DocumentCategory.cadFiles:
					return translations.technicalDrawings
				case DocumentCategory.userManuals:
					return translations.userManuals
				case DocumentCategory.complianceCertificates:
					return translations.compliance
				case DocumentCategory.safetyInstructions:
					return translations.safetyInformations
				default:
					return 'Other'
			}
		})
		const types = Array.from(new Set(categoryName))
		return types
	}, [documents])

	const filters = useMemo(
		() => [
			{
				key: 'products',
				label: translations.productsSolutions,
				items: [...products, ...procuctSearchResults],
			},
			{
				key: 'accessories',
				label: translations.accessories,
				items: [...accessories],
			},
			{
				key: 'solutions',
				label: translations.solutions,
				items: solutionSearchResults,
			},
			{
				key: 'pages',
				label: translations.otherPages,
				items: allSiteSearchResults,
			},
			{
				key: 'articles',
				label: translations.articles,
				items: articles,
			},
			{
				key: 'cadFiles',
				label: translations.technicalDrawings,
				items: cadFiles,
			},
			{
				key: 'userManuals',
				label: translations.userManuals,
				items: userManuals,
			},
			{
				key: 'safetyInstructions',
				label: translations.safetyInformations,
				items: safetyInstructions,
			},
			{
				key: 'complianceCertificates',
				label: translations.compliance,
				items: complianceCertificates,
			},
			{
				key: 'explodedViews',
				label: translations.explodedViews,
				items: explodedViews,
			},
			{
				key: 'dt',
				label: 'Digital Twin',
				items: dt
					? [
							{
								id: 'dt',
								title: `Digital twin - ${dt.serialNumber}`,
								bodyExcerpt: `${dt.family} ${dt.name ? ' - ' + dt.name : ''}`,
								imageSrc: '/images/document-text.svg',
								url: `/digital-twin/${dt.partNumber}?sn=${dt.serialNumber}`,
							},
						]
					: [],
			},
		],
		[
			products,
			procuctSearchResults,
			accessories,
			articles,
			documents,
			allSiteSearchResults,
			dt,
		],
	)

	const handleFilterSelection = (key: string) => {
		const element = document.getElementById(`${isModal ? 'modal_' : ''}${key}`)
		if (element) {
			const offset = 120

			if (isModal) {
				// For modal context, scroll within the modal container
				const modalContainer = element.closest('.overflow-y-auto')
				if (modalContainer) {
					const containerRect = modalContainer.getBoundingClientRect()
					const elementRect = element.getBoundingClientRect()
					const relativeTop = elementRect.top - containerRect.top
					const scrollTop = modalContainer.scrollTop + relativeTop

					modalContainer.scrollTo({
						top: scrollTop,
						behavior: 'smooth',
					})
				}
			} else {
				// For regular page context, scroll the window
				const elementPosition = element.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.pageYOffset - offset

				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth',
				})
			}
		}
	}

	function handleURL(url: string) {
		const langPrefix = `/${lang}/`
		return url.includes(langPrefix) || url.startsWith('http')
			? url
			: `/${lang}${url}`
	}

	const filteredFilters = useMemo(() => {
		return filters.filter((filter) => filter.items.length > 0)
	}, [filters])

	return (
		<div
			className={cn('flex flex-col h-full', {
				'lg:flex-row lg:gap-10 lg:items-start lg:-ml-36': !isModal,
			})}
		>
			<div
				className={cn(
					'flex flex-wrap ml-2 mb-3 text-gray-300 flex-shrink-0 items-center gap-2',
					{
						'lg:flex-col lg:items-start lg:sticky lg:top-28 lg:z-10 lg:bg-white lg:py-4 lg:self-start w-auto lg:w-32':
							!isModal,
						'sticky top-0': isModal,
					},
				)}
			>
				{!isModal && (
					<div className="w-full text-center lg:text-left text-md font-medium text-gray-900">{`${resultsCount} ${translations.results}`}</div>
				)}
				<div
					className={cn('flex flex-wrap gap-2 w-full justify-center', {
						'lg:flex-col lg:items-start': !isModal,
					})}
				>
					{filteredFilters.map((filter) => (
						<button
							className={cn(
								'btn pl-2 pr-0.5 py-0.5 border border-gray-100 text-gray-600',
								{
									'bg-gray-50 text-gray-300 border-gray-50 cursor-not-allowed':
										filter.items.length === 0,
									'pl-3 pr-2 py-2': !isModal,
								},
							)}
							disabled={filter.key !== 'all' && filter.items.length === 0}
							onClick={() => handleFilterSelection(filter.key)}
							key={filter.key}
						>
							<div
								className={cn('text-xs', { 'text-sm text-gray-500': !isModal })}
							>
								{filter.label}
							</div>
							<div
								className={cn('text-xs py-1 px-2 rounded-full bg-gray-100', {
									'bg-gray-50 text-gray-500': !isModal,
								})}
							>
								{filter.key === 'all' ? count : filter.items.length}
							</div>
						</button>
					))}
				</div>
			</div>

			<div
				className={cn(
					'flex-1 min-h-0 mx-2 pr-2 mb-2 flex flex-col gap-2 w-full',
					{
						'w-auto overflow-y-auto scrollbar scrollbar-thumb-primary scrollbar-track-gray-100 scrollbar-w-1 scrollbar-thumb-rounded-full scrollbar-track-rounded-full':
							isModal,
						'lg:min-h-[calc(100vh-300px)] max-w-lg': !isModal,
					},
				)}
			>
				{filters.map((result) => (
					<div key={result.key}>
						{!!result.items.length && (
							<div
								className="ml-2 mt-5 mb-1 text-sm text-gray-500"
								id={`${isModal ? 'modal_' : ''}${result.key}`}
							>
								{result.label}
							</div>
						)}
						{(result.items as DisplayResult[]).map((item, i) => (
							<a href={handleURL(item.url)} key={i} className="w-full">
								<div className="py-2 px-2 rounded-2xl text-gray-900 hover:bg-gray-50 flex items-center w-full">
									<div
										className={cn('flex gap-3 items-start', {
											'items-center': !item.bodyExcerpt,
										})}
									>
										<div className="w-14 h-14 flex-shrink-0 rounded-lg border border-gray-50 p-1">
											<CustomImage
												src={item.imageSrc}
												alt=""
												width={55}
												height={55}
												className="rounded-lg"
											/>
										</div>
										<div className="flex flex-col gap-1 flex-grow">
											<div className="font-medium">{item.title}</div>
											{item.bodyExcerpt && (
												<div className="text-xs">{item.bodyExcerpt}</div>
											)}
										</div>
									</div>
								</div>
							</a>
						))}
					</div>
				))}

				{documentTypes.map((result) => {
					return (
						<div key={result}>
							<div
								className="mt-5 mb-3 px-6 text-xs text-gray-400"
								id={`${isModal ? 'modal_' : ''}${result}`}
							>
								{result}
							</div>
							{documents.map((doc, i) => (
								<a href={handleURL(doc.url)} key={i}>
									<div className="mx-2.5 px-3 my-0.5 pb-2 rounded-2xl text-gray-900 hover:bg-gray-50">
										<div className="flex gap-3 items-start">
											<div className="w-14 h-14 rounded-lg border border-gray-50 p-1">
												<CustomImage
													src={doc.imageSrc}
													alt=""
													className="mt-1"
													width={55}
													height={55}
												/>
											</div>
											<div className="w-96">
												<div className="mb-1 font-medium">{doc.title}</div>
												{doc.bodyExcerpt && (
													<div className="text-xs">{doc.bodyExcerpt}</div>
												)}
											</div>
										</div>
									</div>
								</a>
							))}
						</div>
					)
				})}
			</div>
		</div>
	)
}
